import { useCallback, useEffect, useState } from "react";

/**
 * The browser's own install offer, kept until somebody asks for it.
 *
 * Chrome and Edge fire `beforeinstallprompt` when they decide the page is installable, and calling
 * `preventDefault` on it stops their own banner and hands us the moment instead. plan.md section 5.8
 * asks for an installable application; this is the part that makes the offer visible somewhere a
 * person would look for it rather than in whatever the browser decided to show.
 *
 * **It does not fire everywhere and the interface is built for that.** iOS has no such event at all:
 * installing is Share, then Add to Home Screen, and no page can trigger it. A desktop that has
 * already installed the app does not fire it either. So `canInstall` is false far more often than it
 * is true, and the control that reads it is absent rather than disabled, because a permanently dead
 * button is worse than no button.
 */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallPrompt {
  canInstall: boolean;
  promptInstall: () => Promise<void>;
}

export function useInstallPrompt(): InstallPrompt {
  const [offer, setOffer] = useState<InstallPromptEvent | null>(null);

  useEffect(() => {
    function capture(event: Event) {
      event.preventDefault();
      setOffer(event as InstallPromptEvent);
    }

    // Once installed the offer is spent, and the browser says so with its own event.
    function installed() {
      setOffer(null);
    }

    window.addEventListener("beforeinstallprompt", capture);
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", capture);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!offer) {
      return;
    }

    await offer.prompt();
    await offer.userChoice;

    // Spent either way. A dismissed prompt cannot be shown again from the same event, so keeping it
    // would leave a button that silently does nothing the second time.
    setOffer(null);
  }, [offer]);

  return { canInstall: offer !== null, promptInstall };
}
